import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { exportGb1SaleReport, getAllBranch } from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import {
  downloadBlob,
  getDefaultExportDate,
  getExportDateParam,
  getExportErrorMessage,
  getFilenameFromContentDisposition,
  getGb1ExportFilename,
} from "./utils";
import { Branch } from "../../types/user.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import Gb1SaleReportFilter from "./Gb1SaleReportFilter";

dayjs.locale("th");

export default function Gb1SaleReport() {
  const { isHeadOfficeAccounting } = useUserRole();
  const defaultDate = useMemo(() => getDefaultExportDate(), []);

  const [date, setDate] = useState<Dayjs>(defaultDate);
  const [selectedRoute, setSelectedRoute] = useState<string>("all");
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [exporting, setExporting] = useState<boolean>(false);

  const onGetAllBranch = useCallback(async () => {
    if (!isHeadOfficeAccounting) return;
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data);
  }, [isHeadOfficeAccounting]);

  useEffect(() => {
    onGetAllBranch();
  }, [onGetAllBranch]);

  // HO ส่ง branch uuid ของสาขาที่เลือก (map จาก company_id) / Branch ไม่ส่ง
  // (BE อิงสาขาจาก JWT)
  const exportBranch = useMemo(() => {
    if (isHeadOfficeAccounting) {
      return branches.find((branch) => branch.company_id === selectedBranch)
        ?.uuid;
    }
    return undefined;
  }, [isHeadOfficeAccounting, branches, selectedBranch]);

  // HO ต้องเลือกสาขาก่อน (branch จำเป็น); Branch ดาวน์โหลดได้เลย
  const canDownload = !isHeadOfficeAccounting || selectedBranch !== "all";

  const handleDownload = async () => {
    setExporting(true);
    try {
      const response = await exportGb1SaleReport({
        date: getExportDateParam(date),
        branch: exportBranch,
        route: selectedRoute,
      });
      const filename =
        getFilenameFromContentDisposition(
          response.headers["content-disposition"],
        ) || getGb1ExportFilename(date);

      downloadBlob(filename, response.data);
    } catch (error) {
      const message = await getExportErrorMessage(error);
      if (message) pushGlobalError(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Typography variant="h6" mb={2}>
        รายงาน GB1
      </Typography>

      <Card sx={{ boxShadow: "none", borderRadius: "12px" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack gap={2}>
            <Gb1SaleReportFilter
              date={date}
              setDate={setDate}
              selectedRoute={selectedRoute}
              setSelectedRoute={setSelectedRoute}
              selectedBranch={selectedBranch}
              setSelectedBranch={setSelectedBranch}
              branches={branches}
            />
            <Box>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={!canDownload || exporting}
                sx={{ whiteSpace: "nowrap" }}
              >
                Download
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
